import {customerContext} from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

const INITIATE_RETURN_URL = '/pages/warranty-initiate-return';

class SavedReturnsTable {
  constructor() {
    this.tableBody = document.querySelector('.saved-returns-table__body');
    this.searchInput = document.getElementById('saved-returns-search-input');
    this.searchButton = document.querySelector('.saved-returns-search__button');
    this.dateRangeInput = document.getElementById('saved-returns-date-range-input');
    this.dateRangeWrapper = document.querySelector('.saved-returns-date-range');
    this.table = document.querySelector('.saved-returns-table');
    this.paginationWrapper = document.querySelector('.saved-returns-pagination-wrapper');
    /** @type {any[]} */
    this.currentReturns = [];

    this.init();
  }

  init() {
    if (!this.tableBody) return;

    this.initDateRange();
    this.initSearch();
    this.initSearchClear();
    this.initActionMenus();
    this.initWarrantyReturnBanner();

    const dateRange = this.dateRangeInput?.value || '90';
    const searchTerm = this.searchInput?.value || '';
    this.getSavedReturns(dateRange, searchTerm);
  }

  /**
   * If warranty_return_submitted cookie exists: show banner, clear cookie, auto-close after 5s.
   */
  initWarrantyReturnBanner() {
    const cookieName = 'warranty_return_submitted';
    const hasCookie = document.cookie.split(';').some((s) => s.trim().startsWith(cookieName + '='));
    if (!hasCookie) return;

    const banner = document.querySelector('.rs-banner--warranty-returns');
    if (!banner) return;

    banner.classList.add('rs-banner--visible');

    document.cookie = `${cookieName}=; path=/; max-age=0`;

    setTimeout(() => {
      const closeBtn = banner.querySelector('.rs-banner__close-button');
      if (closeBtn) {
        closeBtn.click();
      } else {
        banner.classList.remove('rs-banner--visible');
      }
    }, 5000);
  }

  initDateRange() {
    if (!this.dateRangeWrapper) return;

    this.dateRangeWrapper.addEventListener('change', () => {
      const dateRange = this.dateRangeInput?.value || '90';
      const searchTerm = this.searchInput?.value || '';
      this.getSavedReturns(dateRange, searchTerm);
    });

    if (this.dateRangeInput) {
      this.dateRangeInput.addEventListener('change', () => {
        const dateRange = this.dateRangeInput?.value || '90';
        const searchTerm = this.searchInput?.value || '';
        this.getSavedReturns(dateRange, searchTerm);
      });
    }
  }

  initSearch() {
    if (!this.searchButton) return;

    this.searchButton.addEventListener('click', () => {
      const dateRange = this.dateRangeInput?.value || '90';
      const searchTerm = this.searchInput?.value || '';
      this.getSavedReturns(dateRange, searchTerm);
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const dateRange = this.dateRangeInput?.value || '90';
          const searchTerm = this.searchInput?.value || '';
          this.getSavedReturns(dateRange, searchTerm);
        }
      });
    }
  }

  /** Clear (X) control inside the saved returns search field. */
  initSearchClear() {
    const searchInput = this.searchInput;
    if (!searchInput) return;

    if (searchInput.dataset.rsSavedReturnsSearchInputWired !== '1') {
      searchInput.dataset.rsSavedReturnsSearchInputWired = '1';
      searchInput.addEventListener('input', () => {
        this.syncSearchClearUi();
      });
    }

    const inputWrapper = searchInput.closest('.rs-input__wrapper');
    if (!inputWrapper) return;

    if (inputWrapper.dataset.rsSavedReturnsSearchClearWired === '1') {
      this.searchClearBtn = inputWrapper.querySelector('.saved-returns-search-clear');
      this.syncSearchClearUi();
      return;
    }
    inputWrapper.dataset.rsSavedReturnsSearchClearWired = '1';

    const clearLabel =
      typeof window.__t === 'function' ? window.__t('rs_profile_locations.clear_search') : 'Clear search';
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'rs-button-unstyled saved-returns-search-clear rs-hidden';
    clearBtn.setAttribute('aria-label', clearLabel);
    clearBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="rs-icon rs-icon-close" aria-hidden="true" focusable="false">' +
      '<path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    clearBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      searchInput.value = '';
      this.syncSearchClearUi();
      searchInput.focus();
      const dateRange = this.dateRangeInput?.value || '90';
      this.getSavedReturns(dateRange, '');
    });
    inputWrapper.appendChild(clearBtn);
    this.searchClearBtn = clearBtn;
    this.syncSearchClearUi();
  }

  syncSearchClearUi() {
    const searchInput = this.searchInput;
    const clearBtn = this.searchClearBtn;
    if (!searchInput || !clearBtn) return;
    const hasVal = (searchInput.value || '').trim().length > 0;
    clearBtn.classList.toggle('rs-hidden', !hasVal);
    searchInput.classList.toggle('saved-returns-search__input--has-value', hasVal);
  }

  /**
   * Parse date range value to dateFrom/dateTo
   * @param {string} dateRange - 'custom:YYYY-MM-DD:YYYY-MM-DD' or preset days
   * @returns {{dateFrom: string, dateTo: string}}
   */
  calculateDateRange(dateRange) {
    if (dateRange && dateRange.includes('custom')) {
      const [startDate, endDate] = dateRange.replace('custom:', '').split(':');
      return {dateFrom: startDate, dateTo: endDate};
    }
    const dateTo = new Date().toISOString().split('T')[0];
    const dateFromDate = new Date();
    dateFromDate.setDate(dateFromDate.getDate() - parseInt(dateRange || '90', 10));
    const dateFrom = dateFromDate.toISOString().split('T')[0];
    return {dateFrom, dateTo};
  }

  /**
   * Call warranty-returns draft search API
   * @param {string} dateFrom - YYYY-MM-DD
   * @param {string} dateTo - YYYY-MM-DD
   * @param {string} searchTerm - Search term
   * @returns {Promise<any>}
   */
  async getDraftReturnsAPI(dateFrom, dateTo, searchTerm) {
    const soldTo = document.body.dataset.soldTo || '';
    const shipTo = document.body.dataset.shipTo || '';
    const salesArea = customerContext?.sales_area;
    const saRaw = Array.isArray(salesArea) ? salesArea[0]?.fields || salesArea[0] : salesArea?.fields || salesArea || {};
    const distributionChannel = saRaw?.distribution_channel_id || saRaw?.distributionChannel || '';
    const division = saRaw?.division_id || saRaw?.division || '';
    const salesOrg = saRaw?.sales_org_id || saRaw?.salesOrg || '';

    const response = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns/saved/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${window.myJWT}`,
      },
      body: JSON.stringify({
        status: 'DRAFT',
        soldTo,
        shipTo,
        distributionChannel,
        division,
        salesOrg,
        filters: {
          searchTerm: searchTerm || '',
          dateFrom,
          dateTo,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Update results count in header
   * @param {number} count
   */
  updateResultsCount(count) {
    const countEl = document.querySelector('.saved-returns--header__title-count');
    if (countEl) {
      countEl.textContent = window.__t('count_results', { count: count });
    }
  }

  /**
   * Get empty state message based on current filters
   * @param {string} searchTerm - Current search term
   * @returns {string}
   */
  getEmptyStateMessage(searchTerm) {
    if (searchTerm) {
      return 'No results to display. Use the search field or date range to view available records.';
    }
    return "You don't have any saved returns yet. Saved returns will appear here.";
  }

  /**
   * Update empty state with icon and message
   * @param {'search'|'box'} icon - Icon type
   * @param {string} message - Message to display
   */
  updateEmptyState(icon, message) {
    if (!this.tableBody || !this.table) return;

    const iconUrl = this.table.getAttribute(`data-icon-${icon}`);
    const displayMessage = message || 'No results to display. Use the search field or date range to view available records.';

    this.tableBody.innerHTML = `
      <tr class="saved-returns-table__empty">
        <td class="text-center" colspan="5">
          <div class="saved-returns-table__icon">
            <img src="${iconUrl}" alt="" width="24" height="24">
          </div>
          <p class="text-large weight-500 text-brand-black">${displayMessage}</p>
        </td>
      </tr>
    `;
  }

  /**
   * Initialize pagination
   * @param {number} totalItems
   */
  initPagination(totalItems) {
    if (!this.paginationWrapper) return;

    const rows = this.tableBody?.querySelectorAll('.saved-returns-table__row-return') || [];
    const optionButtons = this.paginationWrapper.querySelectorAll('.saved-returns-pagination__rows-options .rs-cart-pagination__rows-option');
    const rowSelectBtn = this.paginationWrapper.querySelector('.saved-returns-pagination__rows-button');
    const prevBtn = document.getElementById('saved-returns-pagination-prev');
    const nextBtn = document.getElementById('saved-returns-pagination-next');
    const pageInfo = this.paginationWrapper.querySelector('.saved-returns-pagination__info');
    const rowsOptions = this.paginationWrapper.querySelector('.saved-returns-pagination__rows-options');

    if (!rows.length || !optionButtons.length || !prevBtn || !nextBtn || !pageInfo) return;

    this._paginationCurrentPage = 1;

    const renderPage = () => {
      const totalItems = this.currentReturns.length;
      const rowsPerPage = parseInt(this.paginationWrapper?.querySelector('.rs-cart-pagination__rows-option.rs-active')?.getAttribute('value') || '10', 10);
      const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
      let currentPage = this._paginationCurrentPage;

      if (currentPage > totalPages) currentPage = totalPages;
      if (currentPage < 1) currentPage = 1;
      this._paginationCurrentPage = currentPage;

      const startIndex = (currentPage - 1) * rowsPerPage + 1;
      const endIndex = Math.min(startIndex + rowsPerPage - 1, totalItems);

      const currentRows = this.tableBody?.querySelectorAll('.saved-returns-table__row-return') || [];
      currentRows.forEach((row) => {
        const idx = parseInt(row.getAttribute('data-index') || '0', 10);
        if (idx >= startIndex && idx <= endIndex) {
          row.classList.remove('rs-hidden');
        } else {
          row.classList.add('rs-hidden');
        }
      });

      pageInfo.textContent = `${startIndex}-${endIndex} of ${totalItems}`;
      prevBtn.disabled = currentPage === 1;
      nextBtn.disabled = currentPage === totalPages;
    };

    if (!this.paginationWrapper.dataset.listenersAdded) {
      this.paginationWrapper.dataset.listenersAdded = 'true';

      nextBtn.addEventListener('click', () => {
        const totalPages = Math.max(1, Math.ceil(this.currentReturns.length / parseInt(this.paginationWrapper?.querySelector('.rs-cart-pagination__rows-option.rs-active')?.getAttribute('value') || '10', 10)));
        if (this._paginationCurrentPage < totalPages) {
          this._paginationCurrentPage++;
          renderPage();
        }
      });

      prevBtn.addEventListener('click', () => {
        if (this._paginationCurrentPage > 1) {
          this._paginationCurrentPage--;
          renderPage();
        }
      });

      optionButtons.forEach((option) => {
        option.addEventListener('click', () => {
          optionButtons.forEach((btn) => btn.classList.remove('rs-active'));
          option.classList.add('rs-active');
          this._paginationCurrentPage = 1;
          const span = rowSelectBtn?.querySelector('span');
          if (span) span.textContent = window.__t('show_count_rows', { count: option.getAttribute('value') });
          renderPage();
        });
      });

      rowSelectBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = rowSelectBtn.getAttribute('aria-expanded') === 'true';
        rowSelectBtn.setAttribute('aria-expanded', String(!isExpanded));
        if (rowsOptions) rowsOptions.style.display = isExpanded ? 'none' : 'block';
      });

      document.addEventListener('click', (e) => {
        if (rowSelectBtn && rowsOptions && !rowSelectBtn.contains(e.target) && !rowsOptions.contains(e.target)) {
          rowSelectBtn.setAttribute('aria-expanded', 'false');
          rowsOptions.style.display = 'none';
        }
      });
    }

    this._paginationCurrentPage = 1;
    renderPage();
  }

  /**
   * Initialize action menu (Edit/Remove) for rows
   */
  // initActionMenus() {
  //   this.tableBody?.addEventListener('click', (e) => {
  //     const editBtn = e.target.closest('[data-saved-returns-edit]');
  //     const removeBtn = e.target.closest('[data-saved-returns-remove]');
  //     const menuTrigger = e.target.closest('.saved-returns-table__action-trigger');

  //     if (editBtn) {
  //       e.preventDefault();
  //       const row = editBtn.closest('tr');
  //       const draftId = row?.getAttribute('data-draft-id') || row?.getAttribute('data-web-id');
  //       if (draftId) {
  //         window.location.href = `${INITIATE_RETURN_URL}?draftId=${encodeURIComponent(draftId)}`;
  //       } else {
  //         window.location.href = INITIATE_RETURN_URL;
  //       }
  //     }

  //     if (removeBtn) {
  //       e.preventDefault();
  //       const row = removeBtn.closest('tr');
  //       const draftId = row?.getAttribute('data-draft-id');
  //       this.handleRemove(draftId, row);
  //     }

  //     if (menuTrigger) {
  //       e.preventDefault();
  //       e.stopPropagation();
  //       const menu = menuTrigger.nextElementSibling;
  //       const isOpen = menu?.classList.contains('is-open');
  //       document.querySelectorAll('.saved-returns-table__action-menu.is-open').forEach((m) => m.classList.remove('is-open'));
  //       if (menu && !isOpen) {
  //         menu.classList.add('is-open');
  //       }
  //     }
  //   });

  //   document.addEventListener('click', () => {
  //     document.querySelectorAll('.saved-returns-table__action-menu.is-open').forEach((m) => m.classList.remove('is-open'));
  //   });
  // }
  initActionMenus() {
  const closeAllMenus = ({ restoreFocus = false } = {}) => {
    document
      .querySelectorAll('.saved-returns-table__action-menu.is-open')
      .forEach((menu) => {
        menu.classList.remove('is-open');

        const trigger = menu.previousElementSibling;

        if (trigger?.matches('.saved-returns-table__action-trigger')) {
          trigger.setAttribute('aria-expanded', 'false');

          if (restoreFocus) {
            trigger.focus();
          }
        }
      });
  };

  this.tableBody?.addEventListener('click', (event) => {
    const editBtn = event.target.closest('[data-saved-returns-edit]');
    const removeBtn = event.target.closest('[data-saved-returns-remove]');
    const menuTrigger = event.target.closest(
      '.saved-returns-table__action-trigger'
    );

    if (editBtn) {
      event.preventDefault();

      const row = editBtn.closest('tr');
      const draftId =
        row?.getAttribute('data-draft-id') ||
        row?.getAttribute('data-web-id');

      closeAllMenus();

      window.location.href = draftId
        ? `${INITIATE_RETURN_URL}?draftId=${encodeURIComponent(draftId)}`
        : INITIATE_RETURN_URL;

      return;
    }

    if (removeBtn) {
      event.preventDefault();

      const row = removeBtn.closest('tr');
      const draftId = row?.getAttribute('data-draft-id');

      closeAllMenus();
      this.handleRemove(draftId, row);

      return;
    }

    if (menuTrigger) {
      event.preventDefault();
      event.stopPropagation();

      const menu = menuTrigger.nextElementSibling;
      const isOpen = menu?.classList.contains('is-open');

      closeAllMenus();

      if (menu && !isOpen) {
        menu.classList.add('is-open');
        menuTrigger.setAttribute('aria-expanded', 'true');

        // Move keyboard focus to the first menu item
        menu
          .querySelector('.saved-returns-table__action-item')
          ?.focus();
      }
    }
  });

  document.addEventListener('click', () => {
    closeAllMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openMenu = document.querySelector(
      '.saved-returns-table__action-menu.is-open'
    );

    if (!openMenu) return;

    event.preventDefault();
    event.stopPropagation();

    closeAllMenus({ restoreFocus: true });
  });
}

  /**
   * Handle remove action: call update API with itemsToDelete.
   * Uses item IDs from row data (from search) when available; otherwise fetches draft first.
   * @param {string} draftId - Warranty return GID (warrantyReturnGadgetId)
   * @param {Element} row
   */
  async handleRemove(draftId, row) {
    if (!draftId) return;
    try {
      const updateRes = await fetch(
        `${getCustomerAppBaseUrl()}/api/warranty-returns/${encodeURIComponent(draftId)}/delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${window.myJWT}`,
          }
        }
      );
      if (updateRes.ok) {
        row?.remove();
        const dateRange = this.dateRangeInput?.value || '90';
        const searchTerm = this.searchInput?.value || '';
        this.getSavedReturns(dateRange, searchTerm);
      } else {
        console.error('Failed to remove draft return');
      }
    } catch (err) {
      console.error('Error removing draft return:', err);
    }
  }

  showLoadingState() {
    if (!this.tableBody) return;

    this.tableBody.innerHTML = `
      <tr class="saved-returns-table__loading">
        <td class="text-center" colspan="5">
          <p class="text-large weight-500 text-brand-black">Loading...</p>
        </td>
      </tr>
    `;
  }

  /**
   * Render table rows from API data
   * @param {any[]} returns - Array of return objects
   */
  updateTable(returns) {
    if (!this.tableBody) return;

    this.tableBody.innerHTML = '';

    const actionMenuHtml = (draftId, webId) => {
      const id = draftId || webId || '';
      return `
        <div class="saved-returns-table__action-cell">
          <button type="button" class="saved-returns-table__action-trigger rs-button-unstyled" aria-haspopup="true" aria-expanded="false" aria-label="Actions">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class="rs-icon rs-icon-action">
              <path d="M1.8457 6.15378C2.86531 6.15378 3.69238 6.98082 3.69238 8.00046C3.69229 9.02002 2.86526 9.84616 1.8457 9.84616C0.826249 9.84604 8.88518e-05 9.01994 0 8.00046C0 6.9809 0.826194 6.1539 1.8457 6.15378ZM8 6.15378C9.01959 6.15381 9.8457 6.98084 9.8457 8.00046C9.84552 9.01991 9.01947 9.84613 8 9.84616C6.9805 9.84616 6.15351 9.01993 6.15332 8.00046C6.15332 6.98082 6.98039 6.15378 8 6.15378ZM14.1543 6.15378C15.1737 6.15402 16 6.98097 16 8.00046C15.9998 9.01978 15.1736 9.84592 14.1543 9.84616C13.1348 9.84616 12.3078 9.01993 12.3076 8.00046C12.3076 6.98082 13.1347 6.15378 14.1543 6.15378Z" fill="currentColor"/>
            </svg>
          </button>
          <div class="saved-returns-table__action-menu" role="menu">
            <button type="button" class="saved-returns-table__action-item" data-saved-returns-edit role="menuitem">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    class="rs-icon rs-icon-edit"
                  >
                    <path d="M9.91699 0.00012207C10.4693 0.000224193 10.9991 0.219932 11.3896 0.610474C11.7801 1.0011 12 1.53082 12 2.08313C11.9999 2.60106 11.807 3.0995 11.4609 3.48157L11.3896 3.55579L10.2393 4.7052C10.2275 4.71964 10.2175 4.73569 10.2041 4.74915C10.1907 4.76259 10.1746 4.77353 10.1602 4.78528L4.10938 10.838C3.91972 11.0271 3.68589 11.1673 3.42969 11.2452L1.05469 11.965L1.05371 11.9659C0.912441 12.0084 0.762201 12.0106 0.619141 11.9747C0.475998 11.9387 0.344725 11.8651 0.240234 11.7609C0.135709 11.6565 0.0616322 11.5251 0.0253906 11.382C-0.0107494 11.2389 -0.0080301 11.0887 0.0341797 10.9474L0.0351562 10.9454L0.754883 8.57141L0.755859 8.56946C0.83452 8.31313 0.975167 8.08006 1.16504 7.89075L7.21777 1.83508C7.22848 1.8222 7.2389 1.8081 7.25098 1.79602C7.26288 1.78414 7.27638 1.77434 7.28906 1.76379L8.44336 0.610474C8.83401 0.219917 9.3646 9.61857e-05 9.91699 0.00012207ZM1.93555 8.66321C1.87231 8.7263 1.82503 8.80437 1.79883 8.88977L1.22656 10.7726L3.1123 10.2003L3.1748 10.1779C3.23566 10.1509 3.29145 10.1128 3.33887 10.0656L9.04297 4.3595L7.63965 2.95618L1.93555 8.66321ZM9.91699 1.09094C9.65416 1.09095 9.40182 1.1953 9.21582 1.38098L8.41113 2.18567L9.81445 3.58899L10.6182 2.78528L10.6846 2.71204C10.8291 2.53557 10.9091 2.31325 10.9092 2.08313C10.9092 1.82012 10.8041 1.56798 10.6182 1.38196C10.4322 1.19596 10.18 1.09104 9.91699 1.09094Z" fill="currentColor"/>
                  </svg>
              Edit
            </button>
            <button type="button" class="saved-returns-table__action-item saved-returns-table__action-item--remove" data-saved-returns-remove role="menuitem">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" class="rs-icon rs-icon-remove" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.0918 0C7.5256 9.55115e-05 7.94127 0.172781 8.24805 0.479492C8.55492 0.78637 8.72754 1.20273 8.72754 1.63672V2.18164H10.9092C11.2104 2.18164 11.4551 2.42629 11.4551 2.72754C11.455 3.02869 11.2104 3.27246 10.9092 3.27246H10.3643V10.3633C10.3643 10.7972 10.1916 11.2136 9.88477 11.5205C9.57789 11.8274 9.16153 12 8.72754 12H3.27344C2.83945 12 2.42309 11.8274 2.11621 11.5205C1.80939 11.2136 1.63672 10.7972 1.63672 10.3633V3.27246H1.0918C0.790617 3.27246 0.546006 3.02869 0.545898 2.72754C0.545898 2.42629 0.790551 2.18164 1.0918 2.18164H3.27344V1.63672C3.27344 1.20273 3.44605 0.78637 3.75293 0.479492C4.0597 0.172775 4.47539 0.00011613 4.90918 0H7.0918ZM2.72754 10.3633L2.73047 10.418C2.74293 10.5427 2.79833 10.6596 2.8877 10.749C2.98999 10.8513 3.12877 10.9092 3.27344 10.9092H8.72754C8.8722 10.9092 9.01099 10.8513 9.11328 10.749C9.21551 10.6467 9.27344 10.5079 9.27344 10.3633V3.27246H2.72754V10.3633ZM4.90918 4.90918C5.21029 4.90918 5.45486 5.15304 5.45508 5.4541V8.72754C5.45492 9.02865 5.21033 9.27246 4.90918 9.27246C4.60817 9.27231 4.36442 9.02855 4.36426 8.72754V5.4541C4.36448 5.15314 4.6082 4.90934 4.90918 4.90918ZM7.0918 4.90918C7.3927 4.90942 7.6365 5.15319 7.63672 5.4541V8.72754C7.63656 9.0285 7.39274 9.27222 7.0918 9.27246C6.79065 9.27246 6.54606 9.02865 6.5459 8.72754V5.4541C6.54612 5.15304 6.79069 4.90918 7.0918 4.90918ZM4.90918 1.09082C4.76472 1.09094 4.62659 1.14884 4.52441 1.25098C4.42212 1.35327 4.36426 1.49206 4.36426 1.63672V2.18164H7.63672V1.63672C7.63672 1.49222 7.57961 1.35323 7.47754 1.25098C7.38807 1.16151 7.27031 1.10618 7.14551 1.09375L7.0918 1.09082H4.90918Z" fill="currentColor"/>
              </svg>
              Remove
            </button>
          </div>
        </div>
      `;
    };

    const rows = returns.map((returnItem, index) => {
      const webId = returnItem.id ?? '';
      const draftId = returnItem.id ?? returnItem.draftId ?? returnItem.draft_id ?? webId;
      const customerRef = returnItem.customerReference ?? returnItem.customer_reference ?? returnItem.poNumber ?? returnItem.po_number ?? '';
      const dateCreated = returnItem.dateCreated ?? returnItem.date_created ?? returnItem.createdAt ?? returnItem.created_at ?? '';
      const createdBy = returnItem.customerEmail ?? '';

      const items = returnItem.items ?? returnItem.rows ?? [];
      const itemIds = items.map((item) => String(item.id ?? item.itemId ?? '').trim()).filter(Boolean);
      const dataItemIds = itemIds.length ? ` data-item-ids="${itemIds.join(',')}"` : '';
      const dataCustomerRef = customerRef ? ` data-customer-reference="${String(customerRef).replace(/"/g, '&quot;')}"` : '';
      const emailRecips = returnItem.emailRecipients ?? returnItem.email_recipients ?? [];
      const dataEmails = emailRecips.length ? ` data-email-recipients="${JSON.stringify(emailRecips).replace(/"/g, '&quot;')}"` : '';

      let formattedDate = '';
      if (dateCreated) {
        try {
          formattedDate = new Date(dateCreated).toLocaleDateString();
        } catch {
          formattedDate = dateCreated;
        }
      }

      return `
        <tr class="saved-returns-table__row saved-returns-table__row-return" data-index="${index + 1}" data-draft-id="${draftId}" data-web-id="${webId}"${dataItemIds}${dataCustomerRef}${dataEmails}>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Web ID Number</p>${webId || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Customer Reference / PO #</p>${customerRef || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Date Created</p>${formattedDate || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Created By</p>${createdBy || ''}</td>
          <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Action</p>${actionMenuHtml(draftId, webId)}</td>
        </tr>
      `;
    });

    this.tableBody.innerHTML = rows.join('');
  }

  /**
   * Fetch and display saved returns
   * @param {string} dateRange - Date range value
   * @param {string} searchTerm - Search term
   */
  async getSavedReturns(dateRange, searchTerm) {
    if (!dateRange) return;

    const {dateFrom, dateTo} = this.calculateDateRange(dateRange);

    this.showLoadingState();

    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = 'none';
    }

    try {
      const data = await this.getDraftReturnsAPI(dateFrom, dateTo, searchTerm);

      // API may return items, transactions, results, or similar - adjust path as needed
      let returns = data ?? [];

      if (!Array.isArray(returns)) {
        returns = [];
      }

      if (returns && returns.length > 0) {
        this.currentReturns = returns;
        this.updateTable(returns);
        this.updateResultsCount(returns.length);
        this.initPagination(returns.length);

        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = '';
        }
      } else {
        this.currentReturns = [];
        this.updateResultsCount(0);
        const icon = searchTerm ? 'search' : 'box';
        const message = this.getEmptyStateMessage(searchTerm);
        this.updateEmptyState(icon, message);

        if (this.paginationWrapper) {
          this.paginationWrapper.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error fetching saved returns:', error);
      this.currentReturns = [];
      this.updateResultsCount(0);
      this.updateEmptyState('box', 'You don\'t have any saved returns yet. Saved returns will appear here.');
      if (this.paginationWrapper) {
        this.paginationWrapper.style.display = 'none';
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('.saved-returns-table')) return;

  const checkJWT = () => {
    if (window.myJWT) {
      new SavedReturnsTable();
    } else {
      setTimeout(checkJWT, 100);
    }
  };
  checkJWT();
});
